/**
 * Trading-streak frontend helpers — tier metadata, emblem paths, and the
 * static Tailwind class maps for the `--color-tier-*` accent tokens.
 *
 * Mirrors `@morfi/backend-shared` STREAK_TIERS (thresholds 1/3/7/14/30/90/180).
 * Kept frontend-local so components don't pull a backend package.
 */

export interface StreakTierMeta {
  level: number;
  name: string;
  slug: TierSlug;
  minDays: number;
  /** Short milestone descriptor shown in the levels sheet. */
  milestone: string;
}

export type TierSlug = 'spark' | 'ember' | 'flame' | 'blaze' | 'wildfire' | 'inferno' | 'eternal';

export const STREAK_TIERS: readonly StreakTierMeta[] = [
  { level: 1, name: 'Spark', slug: 'spark', minDays: 1, milestone: 'Where every streak starts' },
  { level: 2, name: 'Ember', slug: 'ember', minDays: 3, milestone: 'Building momentum' },
  { level: 3, name: 'Flame', slug: 'flame', minDays: 7, milestone: 'A full week, locked in' },
  { level: 4, name: 'Blaze', slug: 'blaze', minDays: 14, milestone: 'Two weeks strong' },
  {
    level: 5,
    name: 'Wildfire',
    slug: 'wildfire',
    minDays: 30,
    milestone: 'A month of consistency',
  },
  { level: 6, name: 'Inferno', slug: 'inferno', minDays: 90, milestone: 'Three months unbroken' },
  {
    level: 7,
    name: 'Eternal',
    slug: 'eternal',
    minDays: 180,
    milestone: 'Half a year and counting',
  },
] as const;

/**
 * Level (0..7) for a raw day count. The day count is the single source of
 * truth — components derive the tier from this rather than trusting a
 * separately-fetched `streakLevel`, so the number and the styling can never
 * disagree (a stale server `level` next to a live `currentStreak` was what
 * produced the "1 day / No streak yet" broken hybrid).
 */
export function levelForDays(days: number): number {
  let level = 0;
  for (const tier of STREAK_TIERS) {
    if (days >= tier.minDays) level = tier.level;
  }
  return level;
}

/** Tier for a level (1..7), or null for level 0 (no streak). */
export function tierForLevel(level: number): StreakTierMeta | null {
  return STREAK_TIERS.find((t) => t.level === level) ?? null;
}

/** The next tier above `level`, or null when at the top (Eternal). */
export function nextTier(level: number): StreakTierMeta | null {
  return STREAK_TIERS.find((t) => t.level === level + 1) ?? null;
}

/** Days remaining to reach the next tier, or null at max / no streak data. */
export function daysToNextTier(currentStreak: number, level: number): number | null {
  const next = nextTier(level);
  if (!next) return null;
  return Math.max(0, next.minDays - currentStreak);
}

/**
 * Fine progress (0..1) from the current tier's floor to the next tier's floor —
 * drives the loader ring around the emblem. Full ring at level 0 (showing
 * progress to Spark) and at max tier.
 */
export function progressToNextTier(currentStreak: number, level: number): number {
  const next = nextTier(level);
  if (!next) return 1; // Eternal — full ring
  const floor = tierForLevel(level)?.minDays ?? 0;
  const span = next.minDays - floor;
  if (span <= 0) return 1;
  return Math.min(1, Math.max(0, (currentStreak - floor) / span));
}

/** Spans wider than this collapse from one-pip-per-day to a single fill bar. */
export const MAX_STREAK_PIPS = 14;

export interface StreakBarModel {
  /** `pips` → one cell per day in the span; `bar` → a single continuous fill. */
  kind: 'pips' | 'bar';
  /** Days in the current tier → next-tier span (0 once maxed). */
  total: number;
  /** Completed days within the span. */
  filled: number;
  /** Fill ratio 0..1 — drives the continuous bar. */
  ratio: number;
  /** True at the top tier (Eternal): there is no next milestone. */
  maxed: boolean;
}

/**
 * Models the streak card's bottom bar as progress toward the *next* tier — one
 * pip per day in the current span, completed days filled and the remaining
 * ("incomplete") days hollow. Large spans (Wildfire→Inferno is 60 days,
 * Inferno→Eternal 90) would render as unreadable hairlines, so they collapse to
 * a single continuous fill bar instead.
 */
export function streakBarModel(currentStreak: number, level: number): StreakBarModel {
  const next = nextTier(level);
  if (!next) return { kind: 'bar', total: 0, filled: 0, ratio: 1, maxed: true };
  const floor = level === 0 ? 0 : (tierForLevel(level)?.minDays ?? 0);
  const total = next.minDays - floor;
  const filled = Math.min(total, Math.max(0, currentStreak - floor));
  const ratio = total > 0 ? filled / total : 1;
  return { kind: total <= MAX_STREAK_PIPS ? 'pips' : 'bar', total, filled, ratio, maxed: false };
}

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

/**
 * Adds `days` to a 'YYYY-MM-DD' local day, returning 'YYYY-MM-DD'. UTC math
 * keeps the date-only arithmetic free of DST off-by-one drift — we never leave
 * the local-day string space, so the wall-clock zone is irrelevant.
 */
export function addLocalDays(dateStr: string, days: number): string {
  const parts = dateStr.split('-');
  const base = new Date(Date.UTC(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2])));
  base.setUTCDate(base.getUTCDate() + days);
  return `${base.getUTCFullYear()}-${pad2(base.getUTCMonth() + 1)}-${pad2(base.getUTCDate())}`;
}

export interface ProjectedMilestone {
  level: number;
  name: string;
  slug: TierSlug;
  /** 'YYYY-MM-DD' local day the tier is reached, assuming a daily streak. */
  date: string;
  /** Days from today until the tier is reached (always ≥ 1). */
  daysAway: number;
}

/**
 * Future dates the user reaches each not-yet-earned tier, assuming the streak
 * advances by one every day from `today` (a 'YYYY-MM-DD' local day). The gap is
 * always ≥ 1 for a not-yet-reached tier, so a milestone never lands on today.
 */
export function projectThresholdDates(currentStreak: number, today: string): ProjectedMilestone[] {
  const level = levelForDays(currentStreak);
  return STREAK_TIERS.filter((tier) => tier.level > level).map((tier) => {
    const daysAway = tier.minDays - currentStreak;
    return {
      level: tier.level,
      name: tier.name,
      slug: tier.slug,
      date: addLocalDays(today, daysAway),
      daysAway,
    };
  });
}

/** Versioned paths keep replacement artwork out of the year-long image cache. */
export function emblemSrc(level: number): string {
  const tier = tierForLevel(level);
  if (!tier) return '/images/streaks/v2/0-none.png';
  return `/images/streaks/v2/${tier.level}-${tier.slug}.png`;
}

// ── Static Tailwind class maps (literal strings so Tailwind generates them) ──

const TIER_TEXT: Record<TierSlug, string> = {
  spark: 'text-tier-spark',
  ember: 'text-tier-ember',
  flame: 'text-tier-flame',
  blaze: 'text-tier-blaze',
  wildfire: 'text-tier-wildfire',
  inferno: 'text-tier-inferno',
  eternal: 'text-tier-eternal',
};

const TIER_STROKE: Record<TierSlug, string> = {
  spark: 'stroke-tier-spark',
  ember: 'stroke-tier-ember',
  flame: 'stroke-tier-flame',
  blaze: 'stroke-tier-blaze',
  wildfire: 'stroke-tier-wildfire',
  inferno: 'stroke-tier-inferno',
  eternal: 'stroke-tier-eternal',
};

const TIER_BG: Record<TierSlug, string> = {
  spark: 'bg-tier-spark',
  ember: 'bg-tier-ember',
  flame: 'bg-tier-flame',
  blaze: 'bg-tier-blaze',
  wildfire: 'bg-tier-wildfire',
  inferno: 'bg-tier-inferno',
  eternal: 'bg-tier-eternal',
};

const TIER_BG_SUBTLE: Record<TierSlug, string> = {
  spark: 'bg-tier-spark/20',
  ember: 'bg-tier-ember/20',
  flame: 'bg-tier-flame/20',
  blaze: 'bg-tier-blaze/20',
  wildfire: 'bg-tier-wildfire/20',
  inferno: 'bg-tier-inferno/20',
  eternal: 'bg-tier-eternal/20',
};

/** Tailwind `text-tier-<slug>` for a level, or `text-foreground-tertiary` at level 0. */
export function tierTextClass(level: number): string {
  const tier = tierForLevel(level);
  return tier ? TIER_TEXT[tier.slug] : 'text-foreground-tertiary';
}

/** Tailwind `stroke-tier-<slug>` for a level, or `stroke-border-primary` at level 0. */
export function tierStrokeClass(level: number): string {
  const tier = tierForLevel(level);
  return tier ? TIER_STROKE[tier.slug] : 'stroke-border-primary';
}

/** Tailwind `bg-tier-<slug>` for a level, or `bg-background-tertiary` at level 0. */
export function tierBgClass(level: number): string {
  const tier = tierForLevel(level);
  return tier ? TIER_BG[tier.slug] : 'bg-background-tertiary';
}

/** Tinted (20% opacity) `bg-tier-<slug>/20` for a level — a soft fill the emblem reads on. */
export function tierBgSubtleClass(level: number): string {
  const tier = tierForLevel(level);
  return tier ? TIER_BG_SUBTLE[tier.slug] : 'bg-background-tertiary';
}
